import { createReducer, on } from '@ngrx/store';
import {
  clearThreadList,
  closeActiveThread,
  createNewThread,
  deleteReviewMaterialDocuments,
  deselectActiveMessage,
  findMessageFromEntity,
  getSubmissionSitesSuccess,
  loadingMessageThreads,
  loadingSubmission,
  loadingSubmissionList,
  loadingWorkspaceTypes,
  reloadThread,
  setReviewMaterialDocuments,
  storeActiveSubmissionId,
  storeActiveThread,
  storeLoadingReviewMaterials,
  storeMaterialDocumentTypes,
  storeReviewMaterials,
  storeSubmission,
  storeSubmissionList,
  storeThreadList,
  storeWorkspaceTypes,
  updateStoreReviewMaterials,
  updateSubmissionStatus,
  updatingReviewerDocumentList,
  uploadingReviewerDocument,
} from './submission.actions';
import { Submission } from '../models/submission.model';
import { WorkspaceType } from '@admin/questionnaire/questionnaire.service';
import { Thread } from '@data-access/submissions';

export const featureKey = 'submission';

export interface EntityLoadingState {
  loading: boolean;
  id?: string;
}

export interface SubmissionState {
  submissionList: Submission[];
  activeSubmissionId?: string;
  submission?: Submission;
  loadingSubmission: EntityLoadingState;
  loadingSubmissionList: boolean;
  workspaceTypes: WorkspaceType[];
  loadingWorkspaceTypes: boolean;
  hasNotificationsDetails?: boolean;
  hasNotificationsDocuments?: boolean;
  threads?: any[];
  loadingMessages?: boolean;
  activeThread?: Thread;
  reviewMaterialDocuments?: any[];
  loadingReviewMaterialDocuments: boolean;
  requiredMaterialDocumentTypes?: any[];
  loadingRequiredMaterialDocumentTypes?: boolean;
  uploadingReviewerDocument: {
    [entityId: string]: {
      [documentId: string]: any;
    };
  };
  sites?: any[];
  sitesStatuses?: any[];
  studySites?: any[];
}

export const initialState: SubmissionState = {
  submissionList: [],
  submission: undefined,
  loadingSubmission: {
    loading: false,
  },
  loadingSubmissionList: false,
  workspaceTypes: [],
  loadingWorkspaceTypes: false,
  threads: [],
  reviewMaterialDocuments: [],
  loadingReviewMaterialDocuments: false,
  requiredMaterialDocumentTypes: [],
  loadingRequiredMaterialDocumentTypes: false,
  uploadingReviewerDocument: {},
  activeThread: undefined,
  hasNotificationsDetails: false,
  hasNotificationsDocuments: false,
};

export const submissionReducer = createReducer(
  initialState,
  on(storeActiveSubmissionId, (state, { id }) => ({
    ...state,
    activeSubmissionId: id,
  })),
  on(storeSubmission, (state, { submission }) => ({
    ...state,
    submission,
    loadingSubmission: {
      loading: false,
    },
  })),
  on(storeSubmissionList, (state, { submissions }) => ({
    ...state,
    submissionList: submissions,
    loadingSubmissionList: false,
  })),
  on(loadingSubmission, (state, { id }) => ({
    ...state,
    loadingSubmission: {
      loading: true,
      id,
    },
  })),
  on(loadingSubmissionList, (state) => ({
    ...state,
    loadingSubmissionList: true,
  })),
  on(storeWorkspaceTypes, (state, { workspaceTypes }) => ({
    ...state,
    loadingWorkspaceTypes: false,
    workspaceTypes,
  })),
  on(loadingWorkspaceTypes, (state) => ({
    ...state,
    loadingWorkspaceTypes: true,
  })),
  on(loadingMessageThreads, (state) => ({
    ...state,
    loadingMessages: true,
  })),
  on(clearThreadList, (state) => {
    return {
      ...state,
      threads: [],
      loadingMessages: false,
    };
  }),
  on(storeThreadList, (state, { messages }) => {
    const unresolvedMessages = messages.filter(
      (message) => !message.isResolved,
    );
    const hasNotificationsDocuments = unresolvedMessages.some((message) =>
      message?.typeOfThread?.includes('document'),
    );
    const hasNotificationsDetails = unresolvedMessages.some((message) =>
      message?.typeOfThread?.includes('question'),
    );
    return {
      ...state,
      threads: messages,
      loadingMessages: false,
      hasNotificationsDocuments,
      hasNotificationsDetails,
    };
  }),
  on(storeActiveThread, (state, { threadId }) => {
    return {
      ...state,
      activeThread: state.threads
        ? state.threads?.find((message) => message.entityId === threadId)
        : [],
    };
  }),
  on(reloadThread, (state, { threadId }) => {
    return {
      ...state,
      activeThread: state.threads
        ? state.threads?.find((message) => message.id === threadId)
        : [],
    };
  }),
  on(createNewThread, (state, { thread }) => ({
    ...state,
    activeThread: thread,
  })),
  on(closeActiveThread, (state) => ({
    ...state,
    activeThread: undefined,
  })),

  on(findMessageFromEntity, (state, { entityID }) => {
    if (state.threads) {
      if (state.threads.find((message) => message.entityId === entityID)) {
        return {
          ...state,
          activeThread: state.threads.find(
            (message) => message.entityId === entityID,
          ),
        };
      }
    } else {
      return {
        ...state,
        activeThread: undefined,
      };
    }

    throw new Error('No messages found');
  }),
  on(
    updateSubmissionStatus,
    (
      state,
      {
        qcStatus,
        todoCounts,
        hasNotificationsDetails,
        hasNotificationsDocuments,
      },
    ) => ({
      ...state,
      qc_status: qcStatus,
      todo_counts: todoCounts,
      hasNotificationsDetails,
      hasNotificationsDocuments,
    }),
  ),

  on(deselectActiveMessage, (state) => ({
    ...state,
    activeThread: undefined,
  })),

  on(storeReviewMaterials, (state, { documents }) => ({
    ...state,
    reviewMaterialDocuments: documents,
    loadingReviewMaterialDocuments: false,
  })),
  on(updateStoreReviewMaterials, (state: SubmissionState, { documents }) => ({
    ...state,
    reviewMaterialDocuments: [...documents, ...state.reviewMaterialDocuments!],
    loadingReviewMaterialDocuments: false,
  })),
  on(storeLoadingReviewMaterials, (state) => ({
    ...state,
    loadingReviewMaterialDocuments: true,
  })),

  on(storeMaterialDocumentTypes, (state, { documentTypes }) => ({
    ...state,
    requiredMaterialDocumentTypes: documentTypes,
  })),

  on(setReviewMaterialDocuments, (state, { document }) => {
    const updatedDocuments = state?.reviewMaterialDocuments?.map((doc) =>
      doc.id === document.id ? document : doc,
    );
    return {
      ...state,
      reviewMaterialDocuments: updatedDocuments,
    };
  }),
  on(deleteReviewMaterialDocuments, (state, { documentId }) => {
    const updatedDocuments = state?.reviewMaterialDocuments?.filter(
      (doc) => doc.id !== documentId,
    );
    return {
      ...state,
      reviewMaterialDocuments: updatedDocuments,
    };
  }),
  on(updatingReviewerDocumentList, (state, { document }) => {
    const updatedDocuments = state?.reviewMaterialDocuments ?? [];
    const reviewMaterialDocuments = [...updatedDocuments];
    reviewMaterialDocuments.unshift(document);
    return {
      ...state,
      reviewMaterialDocuments,
    };
  }),
  on(
    uploadingReviewerDocument,
    (
      state,
      {
        uuid,
        workspaceId,
        filename,
        reviewDocumentTypeId,
        reviewDocumentTypeName,
      },
    ) => ({
      ...state,
      uploadingReviewerDocument: {
        ...state.uploadingReviewerDocument,
        [workspaceId]: {
          ...state.uploadingReviewerDocument[workspaceId],
          [uuid]: {
            id: uuid,
            uploading: true,
            progress: 0,
            name: filename,
            error: null,
            reviewDocumentTypeId,
            reviewDocumentTypeName,
          },
        },
      },
    }),
  ),
  on(getSubmissionSitesSuccess, (state, { sites }) => ({
    ...state,
    sites,
  })),
);
